import axios from 'axios';
import { BASE_URL } from './helpers';

/**
 * Narration fit.
 *
 * The backend measures every TTS take (words ÷ seconds) and keeps the resulting
 * words-per-second rate per (provider, model, voice, speed). From that rate it
 * answers how many words fit into a clip — and, when the text overruns, hands
 * the rewrite to an LLM. Both calls are credit-guarded on the backend, so a
 * refusal (402) surfaces like any other generation error.
 */

export interface NarrationBudgetQuery {
  provider: string;
  model: string;
  voice: string;
  speed: number;
  /** Clip length the narration has to fit into. */
  seconds: number;
}

export interface NarrationBudget {
  /** Words that fit into the requested seconds at the resolved rate. */
  words: number;
  /** The rate behind the budget, in words per second. */
  wps: number;
  /** Measured takes behind the rate — 0 when it is the default rate. */
  samples: number;
  /** `default` = 2.4 wps × speed, used until a take has been measured. */
  source: 'measured' | 'default';
}

export const getNarrationBudget = (query: NarrationBudgetQuery): Promise<NarrationBudget> =>
  axios
    .get<NarrationBudget>(`${BASE_URL}/narration/budget`, { params: query })
    .then((res) => res.data);

export interface NarrationShortenRequest {
  text: string;
  /** The budget to rewrite down to. */
  words: number;
  provider: string;
  model: string;
}

/** LLM rewrite of `text` down to `words`. Returns the proposal, nothing is saved. */
export const shortenNarration = (request: NarrationShortenRequest): Promise<string> =>
  axios
    .post<{ text: string }>(`${BASE_URL}/narration/shorten`, request)
    .then((res) => res.data.text);
